import React, {
  useState,
  useRef,
  useEffect,
  useCallback,
  useMemo,
} from "react";
import Text from "components/Text";
import AssistantCard from "aiAssitant/AssistantCard";
import ChatboxFooter from "aiAssitant/ChatboxFooter";
import {
  exportChat,
  getChatResponse,
} from "repositories/ai-assistant-repository";
import alertImage from "assets/images/alert-circle-black.svg";
import starOutlineIcon from "assets/images/rating-star-outline.svg";
import starMarkedIcon from "assets/images/star-rating-marked.svg";
import aiFlagOutline from "assets/images/ai-flag-outline.svg";
import aiFlagFilled from "assets/images/ai-flag-filled.svg";
import exportChatIcon from "assets/images/export-chat-icon.svg";
import { withTranslation } from "react-i18next";
import CustomFeedbackModal from "components/CustomFeedbackModal";
import ChatAgreement from "aiAssitant/ChatAgreement";
import CustomReportModal from "components/CustomReportModal";
import ImageViewer from "Messenger/pages/TeamConversation/components/Modals/ImageViewer";
import { handleError, handleSuccess, downloadBase64AsFile } from "utils";
import Loader from "components/Loader";
import { UncontrolledTooltip } from "reactstrap";

function ChatboxBody({
  t,
  showAssistantCard,
  setShowAssistantCard,
  messages,
  setMessages,
  selectedOwnerId,
  totalMessageCount,
  chatSessionId,
  setchatSessionId,
  fetchSessionChat,
  sessionChatLoading,
  profile,
  canHandleScroll,
  isExpanded,
}) {
  const chatEndRef = useRef(null);
  const chatScrollRef = useRef(null);
  const streamTimersRef = useRef({});
  const [shouldAutoScroll, setShouldAutoScroll] = useState(true);
  const lastScrollHeightRef = useRef(0);

  const [warningMessage, setWarningMessage] = useState("");
  const [isResponseLoading, setIsResponseLoading] = useState(false);
  const [isFetchingOlder, setIsFetchingOlder] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [openReportModal, setOpenReportModal] = useState(false);
  const [selectedResponse, setSelectedResponse] = useState({});
  const [aiChatMessageId, setAiChatMessageId] = useState(null);
  const [aiChatParentMessageId, setAiChatParentMessageId] = useState(null);
  const [fileViewer, setFileViewer] = useState(false);
  const [fileMessage, setFileMessage] = useState(null);
  const [showLoader, setShowLoader] = useState(false);
  useEffect(() => {
    if (shouldAutoScroll && chatEndRef.current) {
      setTimeout(() => {
        chatEndRef.current.scrollIntoView({ behavior: "smooth" });
      }, 100);
    }
  }, [chatSessionId, shouldAutoScroll]);

  useEffect(() => {
    if (chatSessionId) setShouldAutoScroll(true);
  }, [chatSessionId]);

  useEffect(() => {
    return () => {
      Object.values(streamTimersRef.current).forEach(clearInterval);
    };
  }, []);

  // Memoized typewriter effect to prevent unnecessary re-renders
  const startTypewriterEffect = useCallback(
    (fullText, messageId) => {
      let i = 0;
      const stepMs = 15;
      const charsPerTick = 3;

      const timer = setInterval(() => {
        i = Math.min(fullText.length, i + charsPerTick);

        setMessages((prev) =>
          prev.map((msg) =>
            msg.id === messageId ? { ...msg, text: fullText.slice(0, i) } : msg,
          ),
        );

        if (i >= fullText.length) {
          clearInterval(timer);
          delete streamTimersRef.current[messageId];
        }
        setTimeout(() => {
          chatEndRef.current.scrollIntoView({ behavior: "smooth" });
        }, 100);
      }, stepMs);

      streamTimersRef.current[messageId] = timer;
    },
    [setMessages],
  );

  const getChat = async (newMessage, filePaths) => {
    try {
      const params = {
        message: newMessage,
        OwnerId: selectedOwnerId,
        AiChatSessionId: chatSessionId || 0,
      };

      if (filePaths?.length > 0) params.FilePaths = filePaths;

      setIsResponseLoading(true);
      const resp = await getChatResponse(params);
      setWarningMessage("");

      if (resp?.statusCode === 429) {
        setWarningMessage(resp?.message || "");
        return;
      }

      const fullText = resp?.data?.aiResponse || "";
      setchatSessionId(resp?.data?.aiChatSessionId);
      setAiChatMessageId(resp?.data?.aiChatMessageId);
      setAiChatParentMessageId(resp?.data?.parentMessageId);

      setTimeout(() => {
        const messageId = `bot-${Date.now()}`;
        const botMessage = {
          id: messageId,
          text: "",
          sender: "Ai",
          parentMessageId: resp?.data?.aiChatParentMessageId,
          messageId: resp?.data?.aiChatMessageId,
        };

        setMessages((prev) => [...prev, botMessage]);

        setTimeout(() => {
          startTypewriterEffect(fullText, messageId);
        }, 100);
      }, 1000);
    } catch (error) {
      setWarningMessage(error?.message || "");
    } finally {
      setIsResponseLoading(false);
    }
  };

  const handleSendMessage = (newMessage, filePaths, attachments = []) => {
    if (newMessage?.trim() === "" && attachments.length === 0) return;

    Object.values(streamTimersRef.current).forEach(clearInterval);
    streamTimersRef.current = {};

    setMessages((prev) => [
      ...prev,
      {
        id: `user-${Date.now()}`,
        text: newMessage,
        sender: "User",
        attachments: attachments,
        filePaths: filePaths,
      },
    ]);

    setShouldAutoScroll(true);
    getChat(newMessage, filePaths);
    setShowAssistantCard(false);
    setTimeout(() => {
      chatEndRef.current.scrollIntoView({ behavior: "smooth" });
    }, 100);
  };

  const handleScroll = (e) => {
    if (!canHandleScroll) return;
    const container = e.target;
    const isAtTop = container.scrollTop < 100;

    // Load older messages when at top
    if (
      isAtTop &&
      chatSessionId &&
      messages.length < totalMessageCount &&
      !isFetchingOlder &&
      !sessionChatLoading
    ) {
      setIsFetchingOlder(true);
      lastScrollHeightRef.current = container.scrollHeight;

      fetchSessionChat(chatSessionId, { reset: false }).finally(() => {
        setIsFetchingOlder(false);
        // Maintain scroll position after loading older messages
        setTimeout(() => {
          const newScrollHeight = container.scrollHeight;
          const heightDiff = newScrollHeight - lastScrollHeightRef.current;
          container.scrollTop = heightDiff;
          setShouldAutoScroll(false);
        }, 100);
      });
    }
  };

  const exportAiChat = async (parentMessageId) => {
    try {
      setShowLoader(true);
      const params = {
        AiChatSessionId: chatSessionId,
        AiAssistantMessageId: parentMessageId
          ? parentMessageId
          : aiChatParentMessageId,
        ExportWholeChat: false,
      };
      const resp = await exportChat(params);

      // Handle file download if base64Content is provided
      if (resp?.data?.base64Content && resp?.data?.fileName) {
        const { base64Content, fileName, contentType } = resp.data;

        const downloadSuccess = downloadBase64AsFile(
          base64Content,
          fileName,
          contentType,
        );

        if (downloadSuccess) {
          handleSuccess(t("aiAssistant.fileDownloadedSuccessfully"));
        } else {
          handleError(t("aiAssistant.fileDownloadError"));
        }
      } else {
        handleSuccess(resp?.message || t("aiAssistant.exportCompleted"));
      }
    } catch (error) {
      handleError(error?.message || "");
    } finally {
      setShowLoader(false);
    }
  };

  // Memoized callback functions for complex state updates
  const handleRatingSuccess = useCallback(
    (ratingData) => {
      setMessages((prevMessages) =>
        prevMessages.map((msg) =>
          msg.id === selectedResponse.id
            ? {
                ...msg,
                ratings: [
                  {
                    id: Date.now(),
                    rating: ratingData.rating,
                    commentByUser: ratingData.comment,
                    createdAt: new Date().toISOString(),
                  },
                ],
              }
            : msg,
        ),
      );
    },
    [selectedResponse.id],
  );

  const handleReportSuccess = useCallback(
    (reportData) => {
      setMessages((prevMessages) =>
        prevMessages.map((msg) =>
          msg.id === selectedResponse.id
            ? {
                ...msg,
                reports: [
                  {
                    id: Date.now(),
                    reportType: reportData.report,
                    commentByUser: reportData.comment,
                    createdAt: new Date().toISOString(),
                  },
                ],
              }
            : msg,
        ),
      );
    },
    [selectedResponse.id],
  );

  // Memoized computed values to prevent unnecessary recalculations
  const hasUserMessages = useMemo(() => {
    return messages?.some((msg) => msg.sender === "User");
  }, [messages]);

  const aiChatMessageIdValue = useMemo(() => {
    return selectedResponse?.messageId || aiChatMessageId;
  }, [selectedResponse?.messageId, aiChatMessageId]);

  const renderFormattedMessage = useCallback((text) => {
    if (!text) return null;

    const lines = text.split("\n");
    return lines.map((line, lineIndex) => {
      const segments = line.split(/(\*\*.+?\*\*)/g);
      return (
        <React.Fragment key={`line-${lineIndex}`}>
          {segments.map((segment, segmentIndex) => {
            const boldMatch = segment.match(/^\*\*(.+?)\*\*$/);
            if (boldMatch) {
              return (
                <strong key={`segment-${lineIndex}-${segmentIndex}`}>
                  {boldMatch[1]}
                </strong>
              );
            }

            return (
              <React.Fragment key={`segment-${lineIndex}-${segmentIndex}`}>
                {segment}
              </React.Fragment>
            );
          })}
          {lineIndex < lines.length - 1 && <br />}
        </React.Fragment>
      );
    });
  }, []);

  return (
    <div className="chatbot_body_content">
      {showLoader && <Loader />}
      {!hasUserMessages && (
        <div
          className={`greetings_container ${
            showAssistantCard && Boolean(warningMessage) && !isExpanded
              ? ""
              : "flex-grow-1"
          }`}
        >
          <h2 className="title">{`${t("aiAssistant.hello")} ${
            profile?.firstName
          }`}</h2>
          <p className="subtitle">{t("aiAssistant.needHelp")}</p>
        </div>
      )}
      {profile?.isAiPrivacyPolicyAccepted ? (
        <>
          {showAssistantCard && (
            <AssistantCard
              setShowAssistantCard={setShowAssistantCard}
              onSendMessage={handleSendMessage}
            />
          )}

          <div
            className="chat-messages-wrapper"
            onScroll={handleScroll}
            ref={chatScrollRef}
          >
            {(isFetchingOlder || sessionChatLoading) && (
              <div className="text-placeholder-200 shimmer-animation"></div>
            )}
            {messages?.map((msg, index) => (
              <>
                <div
                  key={`msg-${index}-${msg?.messageId}`}
                  className={`chat-message-bubble ${
                    msg.sender === "User"
                      ? "user-message-bubble"
                      : "bot-message-bubble"
                  }`}
                >
                  {/* Show attachments for user messages */}
                  {msg.sender === "User" &&
                    msg.attachments &&
                    msg.attachments.length > 0 && (
                      <div className="message-attachments">
                        {msg.attachments.map((attachment, attIndex) => (
                          <div
                            key={attIndex}
                            className="message-attachment-item"
                          >
                            {attachment.type.startsWith("image/") ? (
                              <img
                                onClick={() => {
                                  setFileViewer(true);
                                  setFileMessage(attachment);
                                }}
                                src={attachment.blobUrl || attachment.preview}
                                alt={attachment.name}
                                className="message-attachment-image cursor-pointer"
                                onError={(e) => {
                                  e.target.style.display = "none";
                                }}
                              />
                            ) : (
                              <div className="message-attachment-document">
                                <div className="download-icon">⬇</div>
                                <span className="file-name">
                                  {attachment.name}
                                </span>
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                    )}

                  {/* Show message text if exists */}
                  {msg.text && (
                    <Text
                      size="14px"
                      weight="400"
                      color="#2D3245"
                      style={{ whiteSpace: "pre-wrap" }}
                    >
                      {renderFormattedMessage(msg.text)}
                    </Text>
                  )}
                </div>
                {msg.sender !== "User" && (
                  <div>
                    <span
                      className="cursor-pointer mr-2"
                      onClick={() => {
                        setModalOpen(true);
                        setSelectedResponse(msg);
                      }}
                    >
                      {msg?.ratings?.length > 0 ? (
                        <>
                          <img
                            src={starMarkedIcon}
                            alt="starMarkedIcon"
                            id={`ai-chat-rated-${msg.id}`}
                          />
                          <UncontrolledTooltip
                            placement="top"
                            target={`ai-chat-rated-${msg.id}`}
                          >
                            {t("aiAssistant.ratedResponse")}
                          </UncontrolledTooltip>
                        </>
                      ) : (
                        <>
                          <img
                            src={starOutlineIcon}
                            alt="starOutlineIcon"
                            id={`ai-chat-rating-${msg.id}`}
                          />
                          <UncontrolledTooltip
                            placement="top"
                            target={`ai-chat-rating-${msg.id}`}
                          >
                            {t("aiAssistant.rating")}
                          </UncontrolledTooltip>
                        </>
                      )}
                    </span>
                    <span
                      className="cursor-pointer mr-2"
                      onClick={() => {
                        exportAiChat(msg?.parentMessageId);
                      }}
                    >
                      <img
                        src={exportChatIcon}
                        alt="exportChatIcon"
                        id={`ai-chat-export-${msg.id}`}
                      />
                      <UncontrolledTooltip
                        placement="top"
                        target={`ai-chat-export-${msg.id}`}
                      >
                        {t("aiAssistant.export")}
                      </UncontrolledTooltip>
                    </span>
                    <span
                      className="cursor-pointer mr-2"
                      onClick={() => {
                        setOpenReportModal(true);
                        setSelectedResponse(msg);
                      }}
                    >
                      {msg?.reports?.length > 0 ? (
                        <>
                          <img
                            src={aiFlagFilled}
                            alt="aiFlagFilled"
                            id={`ai-chat-report-done-${msg.id}`}
                          />
                          <UncontrolledTooltip
                            placement="top"
                            target={`ai-chat-report-done-${msg.id}`}
                          >
                            {t("aiAssistant.reportedResponse")}
                          </UncontrolledTooltip>
                        </>
                      ) : (
                        <>
                          <img
                            src={aiFlagOutline}
                            alt="aiFlagOutline"
                            id={`ai-chat-report-${msg.id}`}
                          />
                          <UncontrolledTooltip
                            placement="top"
                            target={`ai-chat-report-${msg.id}`}
                          >
                            {t("aiAssistant.report")}
                          </UncontrolledTooltip>
                        </>
                      )}
                    </span>
                  </div>
                )}
              </>
            ))}
            {isResponseLoading && (
              <div className="text-placeholder-200 shimmer-animation"></div>
            )}
            {Boolean(warningMessage) && (
              <div className="yellow-warning-box d-flex">
                <img src={alertImage} alt="blackAlertImage" />
                <Text size="14px" weight="400" color="#2F3245">
                  {warningMessage}
                </Text>
              </div>
            )}
            <div ref={chatEndRef} />
          </div>

          <ChatboxFooter
            onSendMessage={handleSendMessage}
            showAssistantCard={showAssistantCard}
            setShowAssistantCard={setShowAssistantCard}
            messages={messages}
          />
          {modalOpen && (
            <CustomFeedbackModal
              t={t}
              setModalOpen={setModalOpen}
              modalOpen={modalOpen}
              selectedResponse={selectedResponse}
              aiChatMessageId={aiChatMessageIdValue}
              onRatingSuccess={handleRatingSuccess}
            />
          )}
          {openReportModal && (
            <CustomReportModal
              t={t}
              setModalOpen={setOpenReportModal}
              modalOpen={openReportModal}
              selectedResponse={selectedResponse}
              aiChatMessageId={aiChatMessageIdValue}
              onReportSuccess={handleReportSuccess}
            />
          )}
        </>
      ) : (
        <ChatAgreement />
      )}
      {fileViewer && fileMessage && (
        <ImageViewer
          imageUrl={fileMessage?.blobUrl || fileMessage?.preview}
          isOpen={fileViewer}
          closeImageViewerModal={() => setFileViewer(false)}
          hideDownloadIcon={true}
        />
      )}
    </div>
  );
}

export default withTranslation()(ChatboxBody);
